
import StringifiedParameterFormatter from '../formatters/StringifiedParameterFormatter.js';
import { HitData, PitchData, ResultData, ScoringData, PitchResultCombined, ResultDataNew, TaggingData, DataSection, ResultType, BattedBallType, HitReprocessed, PitchReprocessed } from '../enums/index.js';
import { ChartSeries, Series, BaseballResultsProcessor } from '../entities/index.js';
import { ResultPair } from '../valueObjects/index.js';
import { buildFiltersArray } from './SprayChartSeriesProvider.js';
import UniqueParametersForPitchAndHitProvider from './UniqueParametersForPitchAndHitProvider.js';

const getSeriesForContactPointChart = function(
    results, customResultsProcessor,
    {
        // tagging context
        batterIds, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {}
) {
    let chartSeries = [];
    let resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    let chartFilters = buildFiltersArray({
        batterIds, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        outs, balls, strikes, runnersOn
    });

    // match pitch and hit...
    let matchedResults = BaseballResultsProcessor.matchPitchesAndHits(results);
    let pairsWithLocation = resultsProcessor.filterData(matchedResults, chartFilters);
    // this is rare case but possible, so there was hit without pitch matched to it
    //mergeHitsWithoutPitchResult(pairsPerPitchResult);

    const battedBallResults = [];
    const battedBallTypeRPath = [ResultPair.getHitProperty()].concat(ResultData.getDataPath(HitReprocessed.BattedBallTypeR.key));

    for (let i = 0; i < pairsWithLocation.length; i++) {
        const result = pairsWithLocation[i];
        const battedBallType = resultsProcessor.getObjectContent(result, battedBallTypeRPath) || BattedBallType.Unidentified;

        if (!battedBallResults.hasOwnProperty(battedBallType)) {
            battedBallResults[battedBallType] = [];
        }

        battedBallResults[battedBallType].push(result);
    }

    for (const battedBallResultsKey in battedBallResults) {
        const resultsPerCategory = battedBallResults[battedBallResultsKey];
        const currentCategory = new Series(battedBallResultsKey, 2);
    
        let resultsMapped = resultsProcessor.mapCollectionProperties(resultsPerCategory, [
            [ResultPair.getHitProperty(), ResultData.Id.key],
            [ResultPair.getHitProperty(), ResultData.PitchId.key],
            [ResultPair.getHitProperty()].concat(ResultData.getDataPath(HitData.HitPoint.key))
        ]);

        for (let i = 0; i < resultsMapped.length; i++) {
            let formatted = StringifiedParameterFormatter(
                resultsMapped[i][HitData.HitPoint.key], ';', HitData.HitPoint.stringifiedAs, parseFloat
            );

            // Coordinates are relative to the tip of home plate, but on chart point zero of Y is a front of home plate. Length of home plate is 17" (0.4318 in).
            const y = formatted.Y - 0.4318;

            currentCategory.add(resultsMapped[i][ResultData.Id.key], formatted.X, y);
        }

        chartSeries.push(currentCategory);
    }

    return new ChartSeries(chartSeries, Object.keys(battedBallResults));
};

const listUniqueParametersForContactPointChart = function(
    results, customResultsProcessor, {
        // tagging context
        batterIds, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {},
    pitchParameters, hitParameters, pitchToHitParamsToMerge, combinedPitchResult
) {
    pitchParameters = pitchParameters || [
        // tagging context
        [ResultData.BatterTagId.key],
        [ResultData.TagId.key],
        ResultData.getDataPath(PitchData.SwingHandedness.key),
        ResultData.getDataPath(PitchData.Handedness.key),
        ResultData.getDataPath(PitchData.Result.key),
        ResultData.getDataPath(PitchData.Classification.key),
        ResultData.getDataPath(PitchReprocessed.StrikeZonePositionR.key),
        // scoring context
        ResultData.getDataPath(PitchData.BeforePlayOuts.key),
        ResultData.getDataPath(PitchData.BeforePlayBalls.key),
        ResultData.getDataPath(PitchData.BeforePlayStrikes.key),
        ResultData.getDataPath(PitchData.BeforePlayRunnersOn.key),
    ];

    hitParameters = hitParameters || [
        [ResultData.TagId.key],
        ResultData.getDataPath(HitData.SwingHandedness.key),
    ];

    pitchToHitParamsToMerge = pitchToHitParamsToMerge || [
        0, undefined, 1
    ];

    const resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    let chartFilters = buildFiltersArray({
        batterIds, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        outs, balls, strikes, runnersOn,
    });

    // match pitch and hit...
    let matchedResults = BaseballResultsProcessor.matchPitchesAndHits(results);

    let pairsWithLocation = resultsProcessor.filterData(matchedResults, chartFilters);
    
    return UniqueParametersForPitchAndHitProvider(
        pitchParameters.map(x => [ResultPair.getPitchProperty()].concat(x)),
        hitParameters.map(x => [ResultPair.getHitProperty()].concat(x)),
        pitchToHitParamsToMerge,
        pairsWithLocation, // it is ok, hit and pitch filtering is included on parameters level
        pairsWithLocation,
        resultsProcessor,
        combinedPitchResult
    );
};

export default getSeriesForContactPointChart;

export {
    getSeriesForContactPointChart,
    listUniqueParametersForContactPointChart
}